import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useFormikContext } from "formik";
import {
    determineRegRolesFromSelectedCategoryId,
    filterCategoriesBasedOnPresetsOrUserSelections,
    getCategoryFilteringFlags,
} from "@qgiv/p2p-registration";
import {
    selectHasParentRegistration,
    selectShowRemainingForm,
    setAppShowRemainingForm,
} from "../../../redux/slices/appSettingsSlice";
import { selectParentRegistration } from "../../../redux/slices/parentRegistrationSlice";
import { selectAllClassifications } from "../../../redux/slices/classificationsSlice";
import {
    selectAllP2pSettings,
    selectAllowSubRegistrantToChangeRegRoleBasedOnEnabledTeams,
} from "../../../redux/slices/p2pSettingsSlice";
import {
    selectAllRegistration,
    setCurrentRegistrationCategory,
    updateCategoryOnRegistrations,
} from "../../../redux/slices/registrationSlice";
import { selectAllCategories } from "../../../redux/slices/categoriesSlice";
import { selectIsCms } from "../../../redux/slices/configSlice";
import Categories from "./Categories";

const ConnectedCategories = ({ controlId }) => {
    const { values, setFieldValue, setFieldTouched } = useFormikContext();
    const hasParentRegistration = useSelector(selectHasParentRegistration);
    const parentRegistration = useSelector(selectParentRegistration);
    const classifications = useSelector(selectAllClassifications);
    const { enableIndividualRegistration, enableTeams, enableClassifications } =
        useSelector(selectAllP2pSettings);
    const allowSubRegistrantToChangeRegRole = useSelector(
        selectAllowSubRegistrantToChangeRegRoleBasedOnEnabledTeams,
    );
    const {
        currentRegistration,
        classification,
        registrations = [],
        currentRegistrationCategory,
    } = useSelector(selectAllRegistration);
    const isCms = useSelector(selectIsCms);

    // From registration
    const { Classification } = classification;
    const categories = useSelector(selectAllCategories);
    const showRemainingForm = useSelector(selectShowRemainingForm);

    const {
        hasClassifications,
        hasPreSelectedTeamCaptain,
        hasPreSelectedTeamMember,
        isChildParticipant,
    } = getCategoryFilteringFlags({
        Classification,
        currentRegistration,
        enableClassifications,
        enableTeams,
        hasParentRegistration,
        values,
    });

    let updateCategoryOnCurrentRegistration = false;
    if (
        Object.keys(registrations[currentRegistration])?.length > 0 &&
        currentRegistrationCategory?.length > 0 &&
        registrations[currentRegistration]?.Category &&
        registrations[currentRegistration].Category?.length > 0 &&
        currentRegistrationCategory[0] !==
            registrations[currentRegistration].Category[0]
    ) {
        updateCategoryOnCurrentRegistration = true;
    }

    const { isRegRoleIndividualOnly } = determineRegRolesFromSelectedCategoryId(
        {
            selectedCategoryId: values?.Category[0] || "",
            categories,
            enableTeams,
            enableIndividualRegistration,
        },
    );

    const isPrimaryRegistrant = currentRegistration === 0;
    const primaryRegistration = registrations[0];

    const categoriesFilteredBySettingsAndUserSelections =
        filterCategoriesBasedOnPresetsOrUserSelections({
            allowSubRegistrantToChangeRegRole,
            categories,
            Classification,
            classifications,
            enableIndividualRegistration,
            enableTeams,
            hasClassifications,
            hasParentRegistration,
            hasPreSelectedTeamCaptain,
            hasPreSelectedTeamMember,
            isChildParticipant,
            parentRegistration,
            registrations,
        });

    const categoriesProps = {
        allowSubRegistrantToChangeRegRole,
        categories: categoriesFilteredBySettingsAndUserSelections,
        controlId,
        currentRegistration,
        currentRegistrationCategory,
        enableIndividualRegistration,
        enableTeams,
        isPrimaryRegistrant,
        isCms,
        isRegRoleIndividualOnly,
        primaryRegistration,
        values,
        // Formik Actions
        setFieldTouched,
        setFieldValue,
        // Actions
        setAppShowRemainingForm,
        setCurrentRegistrationCategory,
        showRemainingForm,
        updateCategoryOnCurrentRegistration,
        updateCategoryOnRegistrations,
    };

    return <Categories {...categoriesProps} />;
};

ConnectedCategories.propTypes = {
    controlId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        .isRequired,
};

export default ConnectedCategories;
